import { isArray } from "lodash";
import { CustomFormBlock } from "@/types";
import { QuizAnswers, QuizContextState } from "@/pages/quiz/context/types";
import Cookies from "js-cookie";
import { resultsPageBase } from "@/constants";
import { CookiesKey } from "@/types/cookies";
import { valueToQueryParams } from "@/utils";
import { TYPED_ENV } from "@/utils/env";
import { getEntrySourceQueryParam, getEntrySource } from "@/utils/getSessionId";
import { MEMORIAL_DISCOUNT_SEARCH_PARAM } from "@/hooks/useMemorialDiscountCapture";
import { isYounger64 } from "@/utils/validateField/dob";

const DEFAULT_RESULTS_PATHNAME = "resultsv3";
const V0_RESULTS_PATHNAME = "results";
const OS_RESULTS_PATHNAME = "results-os";
const V6_BUNDLE_RESULTS_PATHNAME = "results-bundle";
const V1_RESULTS_PATHNAME = "resultsv1";

export const ORAL_TREATMENT_EXCLUDED_STATES = ["AL", "AR", "MS"];
export const TABLET_TREATMENT_EXCLUDED_STATES = ["AL", "MS"];
export const INJECT_EXCLUDED_STATES = ["MS"];
export const TIRZEPATIDE_TABLET_EXCLUDED_STATES = ["AL", "MS"];
export const TIRZEPATIDE_INJECTABLE_EXCLUDED_STATES = ["AL", "MS"];

export const BUNDLE_EXCLUDED_STATES = ["AL", "MS"];
export const MICRODOSE_EXCLUDED_STATES = ["AL", "MS", "VA"];

export enum TREATMENT_OPTION_ID {
  TIRZEPATIDE_TABLET = "TIRZEPATIDE_TABLET",
  TABLET = "TABLET",
  IS = "IS",
  OS = "OS",
  IT = "IT",

  IT_MICRODOSE = "IT_MICRO",
  IS_MICRODOSE = "IS_MICRO",
  // KIT = "KIT",
}

export const QuizIDs = {
  12000: "12000",
  15000: "15000",
} as const;

const getSelectedProductByState = (state: string, product: string) => {
  if (
    ORAL_TREATMENT_EXCLUDED_STATES.includes(state) &&
    product === TREATMENT_OPTION_ID.OS
  ) {
    return TREATMENT_OPTION_ID.IS;
  }

  return product || TREATMENT_OPTION_ID.IS;
};

/* 
    recursive, based on current answers adds conditional blocks to questions array
*/
export const mapBlockTree = (
  blocks: CustomFormBlock[],
  answers: QuizContextState["answers"],
): CustomFormBlock[] => {
  if (blocks && isArray(blocks)) {
    return implementMedicationWaiverConsentQuestion(blocks, answers)
      .map((block) => {
        const selectedAnswer =
          (answers[block.id]?.value as { answer: string })?.answer ??
          answers[block.id]?.value ??
          "";

        if (
          selectedAnswer &&
          Object.values(block.attributes.conditionalBlocks ?? {}).length
        ) {
          const availableBlocks =
            block.attributes.conditionalBlocks[selectedAnswer as string];

          return [block, ...mapBlockTree(availableBlocks || [], answers)];
        }
        //
        return [block];
      })
      .flat();
  }
  return [];
};

/* 
    recursive, extract responses saved to server on quiz init 
*/
export const getInitialAnswers = (blocks: CustomFormBlock[]) => {
  if (blocks && isArray(blocks)) {
    return blocks.reduce(
      (initialAnswers, question) => {
        if (question.is_answered) {
          const conditionalsAnswers: QuizContextState["answers"] =
            getInitialAnswers(
              Object.values(
                question.attributes?.conditionalBlocks ?? {},
              ).flat(),
            ) ?? {};

          // Monthly check-in questions doesn't have 'value' field.
          let questionValue =
            (question?.answers[0]?.value as any) || question?.answers[0];

          if (questionValue?.answer && !questionValue.inputValue) {
            questionValue = questionValue.answer;
          }
          return {
            ...initialAnswers,
            ...conditionalsAnswers,
            [question.id]: {
              value: questionValue,
              isAnswered: true,
              questionId: question.id,
              blockName: question.name,
            },
          };
        }

        return initialAnswers;
      },
      {} as QuizContextState["answers"],
    );
  }

  return {};
};

export const implementMedicationWaiverConsentQuestion = (
  blocks: CustomFormBlock[],
  answers: QuizContextState["answers"],
) => {
  const dobAnswer = Object.values(answers).find(
    (block) => block?.blockName === "custom-dob",
  );

  const dobAnswerValue = (
    dobAnswer?.value as unknown as {
      dob: string | null;
    }
  )?.dob;

  const isYoungerThan64 = isYounger64(dobAnswerValue);

  if (isYoungerThan64) {
    return blocks.filter((block) => block.name !== "medication-waiver-consent");
  }

  return blocks;
};

/* 
  different quizzes may use different result pages
  and different logic of passing params 
*/

const getCpl = (url: URL, answersObj: QuizAnswers) => {
  const isOsClonePage = document.location.pathname?.endsWith("9505");
  const isRClonePage = document.location.pathname?.endsWith("9507");
  const answers = Object.values(answersObj);

  const isCpaPage = answers?.find((a) => a.blockName === "contacts-fields-cpa");
  const isFreeCartPage = answers?.find(
    (a) => a.blockName === "shipping-fields-checkout",
  );

  if (isFreeCartPage) {
    const shippingAnswer = answers?.find(
      (a) => a.blockName === "shipping-fields-checkout",
    );

    const contactAnswer = answers?.find(
      (a) => a.blockName === "contacts-fields-checkout",
    );

    const params = valueToQueryParams({
      ...((shippingAnswer?.value as Record<string, string>) ?? {}),
      ...((contactAnswer?.value as Record<string, string>) ?? {}),
      lastName: ((contactAnswer?.value as Record<string, string>) ?? {})
        ?.secondName,
    });

    // redirect to free checkout results
    return `${resultsPageBase}/checkout-v0?${getEntrySourceQueryParam()}&${params}`;
  }

  if (isCpaPage) {
    Cookies.set(CookiesKey.PASSED_CPA_QUIZ, "true");

    const params = valueToQueryParams(
      (isCpaPage?.value as Record<string, string>) ?? {},
    );

    let resultsSubPath = "resultsv3";

    const searchParams = new URLSearchParams(window.location.search);
    const redirectParam = searchParams.get("resultsPage");

    if (redirectParam) resultsSubPath = redirectParam;

    if (isOsClonePage) resultsSubPath = "results-os";
    if (isRClonePage) resultsSubPath = "resultsv4";

    const href = new URL(`${resultsPageBase}/${resultsSubPath}`);
    href.search = `${getEntrySourceQueryParam()}&${params}`;

    // redirect to CPA results
    return href.toString();
  }

  const isFire = Cookies.get(CookiesKey.IS_FIRE);

  let cplParams = "";

  // meaning in cookie we set invalid string
  if (isFire === "1" || isFire === "0") {
    cplParams = `isFire=${isFire}`;
  }
  // redirect to CPL results
  const cplHref = new URL(`${resultsPageBase}/results`);
  cplHref.search = `${getEntrySourceQueryParam()}&${cplParams}`;

  return cplHref.toString();
};

const getV0ResultsUrl = (url: URL) => {
  url.pathname = V0_RESULTS_PATHNAME;

  return url.toString();
};

const getResultsUrl = (url: URL, answersObj: QuizAnswers) => {
  const product = Object.values(answersObj).find(
    (a) =>
      a.blockName === "custom-select-treatment-option-bundle" ||
      a.blockName === "custom-select-treatment-option",
  ) as { value: { selectedOptionId: string } };

  const addressData = Object.values(answersObj).find(
    (a) =>
      a.blockName === "custom-contact-for-available-plans" ||
      a.blockName === "custom-contact-for-available-plans-address",
  ) as { value: Record<string, string> };

  const quizState = Object.values(answersObj).find(
    (a) => a.blockName === "select-state",
  )?.value as string;

  const finalState = addressData?.value?.state || quizState;
  const isExcludedBundleState = BUNDLE_EXCLUDED_STATES.includes(finalState);
  const defaultPathname = V1_RESULTS_PATHNAME;

  url.pathname = !isExcludedBundleState
    ? V6_BUNDLE_RESULTS_PATHNAME
    : defaultPathname;

  // check current search params for isCommerce param
  const currentUrlParams = new URLSearchParams(window.location.search);

  const isCommerce = currentUrlParams.get("commerce");
  const queryProductId = currentUrlParams.get("product_id");

  if (isCommerce) {
    url.searchParams.set("commerce", "true");
  }

  const emailData = Object.values(answersObj).find(
    (a) =>
      a.blockName === "custom-email-field" ||
      a.blockName === "sign-up-advanced",
  ) as { value: Record<string, string> };

  const dobData = Object.values(answersObj).find(
    (a) => a.blockName === "custom-dob",
  ) as { value: Record<string, string> };

  const fullNameDobData = Object.values(answersObj).find(
    (a) => a.blockName === "contact-full-name",
  ) as { value: Record<string, string> };

  const heightAndWeight = Object.values(answersObj).find(
    (a) => a.blockName === "height-weight-input-fields-with-bmi",
  ) as { value: Record<string, string> };

  const params = {
    dob: dobData?.value?.dob ?? "",
    ...(emailData?.value ?? {}),
    ...(fullNameDobData?.value ?? {}),
    ...(addressData?.value ?? {}),
    state: finalState ?? "",
    selectedProduct:
      queryProductId ||
      getSelectedProductByState(
        finalState,
        product?.value?.selectedOptionId,
      ).toLowerCase(),
    currentWeight: heightAndWeight?.value?.weight ?? "0",
  };

  // params which user landed on page
  const originalUrlParams = new URLSearchParams(
    decodeURIComponent(Cookies.get(CookiesKey.EV_ENTRY_PARAMS) ?? ""),
  );

  for (const [key, value] of originalUrlParams?.entries() ?? []) {
    url.searchParams.set(key, value);
  }

  if (Cookies.get(CookiesKey.MEMORIAL_DISCOUNT)) {
    url.searchParams.set(MEMORIAL_DISCOUNT_SEARCH_PARAM, "true");
  }

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  return url.toString();
};

const getOSResultsUrl = (url: URL, answers: QuizAnswers) => {
  url.pathname = OS_RESULTS_PATHNAME;

  return getContactFunnelQueryParams(url, answers);
};

const getCheckoutFunnelUrl = (url: URL, answers: QuizAnswers) => {
  // adjust results page
  url.pathname = "checkout-v0";

  // add checkout specific params
  const shippingData = Object.values(answers).find(
    (a) => a.blockName === "shipping-fields-checkout",
  ) as { value: Record<string, string> };

  const contactsData = Object.values(answers).find(
    (a) => a.blockName === "contacts-fields-checkout",
  ) as { value: Record<string, string> };

  const params = {
    ...(contactsData?.value ?? {}),
    ...(shippingData?.value ?? {}),
  };

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  return url.toString();
};

const getContactFunnelQueryParams = (url: URL, answers: QuizAnswers) => {
  // Add any necessary query parameters based on answers
  const contactData = Object.values(answers).find(
    (a) => a.blockName === "contacts-fields-cpa",
  ) as { value: Record<string, string> };

  const dobData = Object.values(answers).find(
    (a) => a.blockName === "custom-dob",
  ) as { value: Record<string, string> };

  const params = {
    ...(contactData?.value ?? {}),
    dob: dobData?.value?.dob ?? "",
  };

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  return url.toString();
};

const getContactFunnelUrl = (url: URL, answers: QuizAnswers) => {
  url.pathname = "resultsv3";

  return getContactFunnelQueryParams(url, answers);
};

const getOTResultsUrl = (url: URL, answers: QuizAnswers) => {
  url.pathname = "results-ot";

  return getContactFunnelQueryParams(url, answers);
};

type QuizUrlResolver = (url: URL, answers: QuizAnswers) => string;

const quizUrlResolvers: Record<string, QuizUrlResolver> = {
  "9500": getV0ResultsUrl,
  "9502": getContactFunnelUrl,
  "9504": getOTResultsUrl,
  "9505": getOSResultsUrl,
  "9506": getCpl,
  [QuizIDs[12000]]: getResultsUrl,
  [QuizIDs[15000]]: getResultsUrl,
};

export const getQuizResultPageUrl = (answers: QuizAnswers, quizId?: string) => {
  const url = new URL(
    TYPED_ENV.VITE_FRONT_BASE_URL ?? "https://front.skinnyrx.com",
  );

  // params used by all quizzes
  url.searchParams.append("entry_source", getEntrySource() ?? "unknown");
  // default pathname
  url.pathname = DEFAULT_RESULTS_PATHNAME;

  if (!quizId) {
    return url.toString();
  }

  const cb = quizUrlResolvers[quizId];

  if (cb) {
    return cb(url, answers);
  }

  return url.toString();
};

export const questionNamesForAnswersToBeTransformed = [
  "contacts-fields",
  "height-weight-input-fields",
  "custom-single-choice",
  "custom-multiple-choice",
];
